import React, { useState } from 'react';

import { BACKEND_URL } from '../config';

// Eye icons as SVG
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);

function AuthScreen({ onLogin, onRegister, onNavigate }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // IDs for accessible label/input association
  const IDS = {
    loginEmail: 'auth-email',
    loginPassword: 'auth-password',
    signupName: 'auth-name',
    signupConfirmPassword: 'auth-confirm-password',
    forgotEmail: 'auth-forgot-email',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate confirm password on sign up
    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        await onLogin(email, password);
      } else {
        await onRegister(email, password, name);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMessage('');
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });

      if (response.ok) {
        setForgotMessage('If the email exists, a reset link has been sent. Check your email.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotEmail('');
          setForgotMessage('');
        }, 3000);
      }
    } catch (err) {
      setForgotMessage('Error sending reset link. Please try again.');
    }
    setLoading(false);
  };

  const passwordInputStyle = {
    position: 'relative'
  };

  const toggleButtonStyle = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
    opacity: 0.7,
    transition: 'opacity 0.2s'
  };

  if (showForgotPassword) {
    return (
      <div className="auth-screen">
        <div className="auth-container">
          <h1 className="heading-1">Forgot Password</h1>
          <p className="body-large" style={{ marginBottom: '32px', color: 'var(--text-secondary)' }}>
            Enter your email to receive a password reset link
          </p>

          <form onSubmit={handleForgotPassword} className="auth-form">
            <div className="form-group">
              <label className="body-medium" htmlFor={IDS.forgotEmail}>Email</label>
              <input
                id={IDS.forgotEmail}
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="form-input"
              />
            </div>

            {forgotMessage && (
              <div className="success-message" style={{
                background: '#d1fae5',
                color: '#065f46',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {forgotMessage}
              </div>
            )}

            <button type="submit" className="btn-primary" disabled={loading} style={{ marginBottom: '16px' }}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <button
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="btn-secondary"
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-container">
        <h1 className="heading-1">HealthTrail</h1>
        <p className="body-large" style={{ marginBottom: '32px', color: 'var(--text-secondary)' }}>
          Track your food and exercise journey
        </p>

        <div className="auth-tabs">
          <button
            type="button"
            className={isLogin ? 'auth-tab active' : 'auth-tab'}
            onClick={() => { setIsLogin(true); setError(''); }}
          >
            Login
          </button>
          <button
            type="button"
            className={!isLogin ? 'auth-tab active' : 'auth-tab'}
            onClick={() => { setIsLogin(false); setError(''); }}
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {!isLogin && (
            <div className="form-group">
              <label className="body-medium" htmlFor={IDS.signupName}>Name</label>
              <input
                id={IDS.signupName}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="form-input"
              />
            </div>
          )}

          <div className="form-group">
            <label className="body-medium" htmlFor={IDS.loginEmail}>Email</label>
            <input
              id={IDS.loginEmail}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="form-input"
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="body-medium" htmlFor={IDS.loginPassword}>Password</label>
            <div style={passwordInputStyle}>
              <input
                id={IDS.loginPassword}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="form-input"
                style={{ paddingRight: '44px' }}
                minLength="6"
                data-testid="auth-password"
                autoComplete={isLogin ? 'current-password' : 'new-password'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={toggleButtonStyle}
                onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                onMouseOut={(e) => e.currentTarget.style.opacity = '0.7'}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="toggle-password"
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          {!isLogin && (
            <div className="form-group">
              <label className="body-medium" htmlFor={IDS.signupConfirmPassword}>Confirm Password</label>
              <div style={passwordInputStyle}>
                <input
                  id={IDS.signupConfirmPassword}
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="form-input"
                  style={{ paddingRight: '44px' }}
                  minLength="6"
                  data-testid="auth-confirm-password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={toggleButtonStyle}
                  onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                  onMouseOut={(e) => e.currentTarget.style.opacity = '0.7'}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  data-testid="toggle-confirm-password"
                >
                  {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">{error}</div>
          )}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Sign Up')}
          </button>

          {isLogin && (
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="forgot-password-link"
            >
              Forgot Password?
            </button>
          )}
        </form>

        {/* Legal Links */}
        {onNavigate && (
          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              By signing up, you agree to our{' '}
              <button
                type="button"
                onClick={() => onNavigate('terms')}
                style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', textDecoration: 'underline', fontSize: '12px', padding: 0 }}
              >
                Terms of Service
              </button>
              {' '}and{' '}
              <button
                type="button"
                onClick={() => onNavigate('privacy')}
                style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', textDecoration: 'underline', fontSize: '12px', padding: 0 }}
              >
                Privacy Policy
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthScreen;
